/**
 * Reads behind the home page tiles (#2640). One TanStack query per source,
 * each keyed with the account id. None of them blocks the team list: a
 * failed or missing read gives an empty result and the tiles render without
 * those facts (an old API, a 404, a network error).
 *
 *   team-overview   `GET /api/account/me/team-overview?from=` (every team)
 *   shifts          `GET /api/account/me/schedule?from&to` (every team)
 *   leaves          `GET /api/account/me/leaves` (own cache entry under the member app's key prefix, so a leave change that invalidates that prefix refreshes it)
 *
 * The last two run only when the account is a member of some team.
 */
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import { type MyLeavesListResponse, meLeavesKey } from "../../api/useMyLeaves";
import { useAuth } from "../../lib/AuthContext";
import type { MemberLeave, MemberShift } from "./homeOverview";
import type { TeamOverviewRow } from "./overviewTypes";

const STALE_MS = 60_000;

export function teamOverviewKey(accountId: string | null, from: string) {
  return ["team-overview", accountId ?? "", from] as const;
}

export function homeShiftsKey(accountId: string | null, from: string, to: string) {
  return ["home-me-shifts", accountId ?? "", from, to] as const;
}

/** One row per team the account belongs to, by team id. */
export function useTeamOverview(from: string, enabled = true) {
  const { accountId } = useAuth();
  const query = useQuery<TeamOverviewRow[]>({
    queryKey: teamOverviewKey(accountId, from),
    enabled: Boolean(accountId) && enabled,
    staleTime: STALE_MS,
    retry: false,
    queryFn: async () => {
      try {
        const body = await apiFetch<unknown>(
          `/api/account/me/team-overview?from=${encodeURIComponent(from)}`,
        );
        return Array.isArray(body) ? (body as TeamOverviewRow[]) : [];
      } catch {
        // Never block the list: an old API or a failed call shows no facts.
        return [];
      }
    },
  });
  const byTeam = new Map<string, TeamOverviewRow>();
  for (const row of query.data ?? []) {
    if (row && typeof row.team_id === "string") byTeam.set(row.team_id, row);
  }
  return { byTeam, isLoading: query.isLoading };
}

export interface HomeMemberFacts {
  assignments: MemberShift[];
  leaves: MemberLeave[];
}

const EMPTY: HomeMemberFacts = { assignments: [], leaves: [] };

/** The member's own shifts and leave, for the member tiles. */
export function useHomeMemberFacts(from: string, to: string, enabled: boolean): HomeMemberFacts {
  const { accountId } = useAuth();
  const on = Boolean(accountId) && enabled;

  const shifts = useQuery<MemberShift[]>({
    queryKey: homeShiftsKey(accountId, from, to),
    enabled: on,
    staleTime: STALE_MS,
    retry: false,
    queryFn: async () => {
      try {
        const body = await apiFetch<{ assignments?: unknown }>(
          `/api/account/me/schedule?from=${from}&to=${to}`,
        );
        return Array.isArray(body?.assignments) ? (body.assignments as MemberShift[]) : [];
      } catch {
        return [];
      }
    },
  });

  const leaves = useQuery<MemberLeave[]>({
    queryKey: [...meLeavesKey(accountId), "home"],
    enabled: on,
    staleTime: STALE_MS,
    retry: false,
    queryFn: async () => {
      try {
        const body = await apiFetch<MyLeavesListResponse>("/api/account/me/leaves");
        return Array.isArray(body?.leaves) ? (body.leaves as MemberLeave[]) : [];
      } catch {
        return [];
      }
    },
  });

  if (!on) return EMPTY;
  return {
    assignments: shifts.data ?? [],
    leaves: leaves.data ?? [],
  };
}
